// src/features/calendar/CalendarGrid.tsx
// Month / week / day / agenda views. Click a slot to create, click an event to open.

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import type { CalendarEvent } from '@core/types/calendar';
import {
  addDays,
  dayKey,
  eventVerticalBounds,
  fmtDate,
  fmtTime,
  groupEventsByDay,
  isSameDay,
  startOfMonth,
  type CalendarViewMode,
  viewRange,
} from './time';

interface CalendarGridProps {
  view: CalendarViewMode;
  anchor: Date;
  events: CalendarEvent[];
  calendarColors: Map<string, string>;
  onSlotClick: (start: Date) => void;
  onEventClick: (ev: CalendarEvent) => void;
  selectedEventId?: string | null;
}

const HOUR_PX = 44;

function EventChip({ ev, color, locale, selected, onClick, showTime }: {
  ev: CalendarEvent;
  color?: string;
  locale: string;
  selected: boolean;
  onClick: () => void;
  showTime: boolean;
}) {
  const cancelled = ev.status === 'EVENT_STATUS_CANCELLED';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-xs leading-4 ${
        selected ? 'ring-2 ring-ring' : ''
      } ${cancelled ? 'line-through opacity-60' : ''} bg-primary/10 text-foreground hover:bg-primary/20`}
      style={color ? { borderLeft: `3px solid ${color}` } : { borderLeft: '3px solid transparent' }}
      title={ev.title}
    >
      {showTime && !ev.is_all_day && (
        <span className="mr-1 text-muted-foreground">{fmtTime(ev.start_time, locale)}</span>
      )}
      {ev.title || '—'}
    </button>
  );
}

export function CalendarGrid({ view, anchor, events, calendarColors, onSlotClick, onEventClick, selectedEventId }: CalendarGridProps) {
  const { t, locale } = useI18n();
  const { start, end } = useMemo(() => viewRange(view, anchor), [view, anchor]);
  const byDay = useMemo(() => groupEventsByDay(events, start, end), [events, start, end]);
  const today = new Date();

  const colorOf = (ev: CalendarEvent) => ev.color || (ev.calendar?.id ? calendarColors.get(ev.calendar.id) : undefined);

  const weekdayLabels = [
    t('calendar.weekday.mon'), t('calendar.weekday.tue'), t('calendar.weekday.wed'),
    t('calendar.weekday.thu'), t('calendar.weekday.fri'), t('calendar.weekday.sat'), t('calendar.weekday.sun'),
  ];

  // ------------------------------------------------------------------ month
  if (view === 'month') {
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) days.push(addDays(start, i));
    const monthStart = startOfMonth(anchor);
    return (
      <div className="flex h-full flex-col">
        <div className="grid grid-cols-7 border-b border-border">
          {weekdayLabels.map((l) => (
            <div key={l} className="px-2 py-1 text-xs font-medium text-muted-foreground">{l}</div>
          ))}
        </div>
        <div className="grid flex-1 grid-cols-7 grid-rows-6">
          {days.map((d) => {
            const evs = byDay[dayKey(d)] ?? [];
            const inMonth = d.getMonth() === monthStart.getMonth();
            return (
              <div
                key={d.toISOString()}
                role="button"
                tabIndex={0}
                aria-label={fmtDate(d, locale)}
                onClick={() => onSlotClick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSlotClick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0));
                  }
                }}
                className={`min-h-[5.5rem] cursor-pointer border-b border-r border-border p-1 align-top hover:bg-muted/50 ${
                  inMonth ? '' : 'bg-muted/30'
                }`}
              >
                <span
                  className={`mb-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                    isSameDay(d, today) ? 'bg-primary font-semibold text-primary-foreground' : inMonth ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {d.getDate()}
                </span>
                <div className="space-y-0.5">
                  {evs.slice(0, 3).map((ev, i) => (
                    <EventChip
                      key={(ev.entity?.id ?? '') + i}
                      ev={ev}
                      color={colorOf(ev)}
                      locale={locale}
                      selected={!!selectedEventId && ev.entity?.id === selectedEventId}
                      onClick={() => onEventClick(ev)}
                      showTime={false}
                    />
                  ))}
                  {evs.length > 3 && (
                    <span className="block px-1.5 text-xs text-muted-foreground">
                      {t('calendar.grid.more', { count: evs.length - 3 })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------ agenda
  if (view === 'agenda') {
    const days: Date[] = [];
    for (let i = 0; i < 30; i++) days.push(addDays(start, i));
    const nonEmpty = days.filter((d) => (byDay[dayKey(d)] ?? []).length > 0);
    if (nonEmpty.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('calendar.grid.agendaEmpty')}</p>
        </div>
      );
    }
    return (
      <div className="h-full space-y-4 overflow-y-auto p-4">
        {nonEmpty.map((d) => (
          <div key={d.toISOString()}>
            <h3 className={`mb-1 text-sm font-medium ${isSameDay(d, today) ? 'text-primary' : 'text-foreground'}`}>
              {fmtDate(d, locale)}
            </h3>
            <div className="space-y-1">
              {(byDay[dayKey(d)] ?? []).map((ev, i) => (
                <button
                  key={(ev.entity?.id ?? '') + i}
                  type="button"
                  onClick={() => onEventClick(ev)}
                  className={`flex w-full items-center gap-3 rounded-md border border-border px-3 py-1.5 text-left text-sm hover:bg-muted ${
                    selectedEventId && ev.entity?.id === selectedEventId ? 'ring-2 ring-ring' : ''
                  }`}
                >
                  <span className="w-28 shrink-0 text-xs text-muted-foreground">
                    {ev.is_all_day
                      ? t('calendar.event.allDay')
                      : `${fmtTime(ev.start_time, locale)} – ${fmtTime(ev.end_time, locale)}`}
                  </span>
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: colorOf(ev) ?? 'hsl(var(--primary))' }}
                    aria-hidden="true"
                  />
                  <span className={`truncate text-foreground ${ev.status === 'EVENT_STATUS_CANCELLED' ? 'line-through opacity-60' : ''}`}>
                    {ev.title || '—'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ------------------------------------------------------------------ day/week
  const dayCount = view === 'day' ? 1 : 7;
  const days: Date[] = [];
  for (let i = 0; i < dayCount; i++) days.push(addDays(start, i));
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* header row with all-day events */}
      <div className="grid border-b border-border" style={{ gridTemplateColumns: `3rem repeat(${dayCount}, minmax(0, 1fr))` }}>
        <div />
        {days.map((d, i) => (
          <div key={d.toISOString()} className="border-l border-border px-1 py-1">
            <span className={`text-xs font-medium ${isSameDay(d, today) ? 'text-primary' : 'text-muted-foreground'}`}>
              {view === 'week' ? `${weekdayLabels[i]} ${d.getDate()}` : fmtDate(d, locale)}
            </span>
            <div className="mt-0.5 space-y-0.5">
              {(byDay[dayKey(d)] ?? []).filter((e) => e.is_all_day).map((ev, j) => (
                <EventChip
                  key={(ev.entity?.id ?? '') + j}
                  ev={ev}
                  color={colorOf(ev)}
                  locale={locale}
                  selected={!!selectedEventId && ev.entity?.id === selectedEventId}
                  onClick={() => onEventClick(ev)}
                  showTime={false}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* time grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid" style={{ gridTemplateColumns: `3rem repeat(${dayCount}, minmax(0, 1fr))` }}>
          {/* hour gutter */}
          <div>
            {hours.map((h) => (
              <div key={h} className="relative border-b border-border text-right" style={{ height: HOUR_PX }}>
                <span className="pr-1 text-[10px] text-muted-foreground">{String(h).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>
          {days.map((d) => {
            const timed = (byDay[dayKey(d)] ?? []).filter((e) => !e.is_all_day);
            return (
              <div key={d.toISOString()} className="relative border-l border-border">
                {hours.map((h) => (
                  <div
                    key={h}
                    role="button"
                    tabIndex={0}
                    aria-label={`${fmtDate(d, locale)} ${String(h).padStart(2, '0')}:00`}
                    onClick={() => onSlotClick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSlotClick(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0));
                      }
                    }}
                    className="cursor-pointer border-b border-border hover:bg-muted/40"
                    style={{ height: HOUR_PX }}
                  />
                ))}
                {timed.map((ev, i) => {
                  const { top, height } = eventVerticalBounds(ev, d);
                  return (
                    <div
                      key={(ev.entity?.id ?? '') + i}
                      className="absolute left-0.5 right-0.5"
                      style={{ top: (top / 60) * HOUR_PX, height: (height / 60) * HOUR_PX }}
                    >
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                        title={ev.title}
                        className={`h-full w-full overflow-hidden rounded border border-primary/30 bg-primary/10 px-1 py-0.5 text-left text-xs text-foreground hover:bg-primary/20 ${
                          selectedEventId && ev.entity?.id === selectedEventId ? 'ring-2 ring-ring' : ''
                        } ${ev.status === 'EVENT_STATUS_CANCELLED' ? 'line-through opacity-60' : ''}`}
                        style={colorOf(ev) ? { borderLeft: `3px solid ${colorOf(ev)}` } : undefined}
                      >
                        <span className="block truncate font-medium">{ev.title || '—'}</span>
                        <span className="block truncate text-muted-foreground">
                          {fmtTime(ev.start_time, locale)} – {fmtTime(ev.end_time, locale)}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
